/* ==========================================================================
   HOPE BASE ADVISORY LLC — ADMIN SETTINGS, USER MANAGEMENT & SIGNATURES
   ========================================================================== */

import { SignaturePad } from './signature-pad.js';

const SETTINGS_API = '/api/settings.php';
let signaturePadInstance = null;

// Default Admin Account Fallback
const DEFAULT_USERS = [
  { id: 1, username: 'admin', full_name: 'Executive Director', role: 'admin', created_at: '2026-08-01' }
];

export function initAdminSettings() {
  const settingsModal = document.getElementById('admin-settings-modal');
  const settingsClose = document.getElementById('admin-settings-close');
  const openSettingsBtn = document.getElementById('admin-open-settings-btn') || document.getElementById('admin-settings-btn');
  const tabBtns = document.querySelectorAll('.settings-tab-btn');
  const tabPanes = document.querySelectorAll('.settings-tab-pane');

  const saveBankBtn = document.getElementById('settings-save-bank-btn');
  const saveSigBtn = document.getElementById('settings-save-sig-btn');
  const clearSigBtn = document.getElementById('settings-clear-sig-btn');
  const uploadSigInput = document.getElementById('settings-upload-sig-file');
  const uploadStampInput = document.getElementById('settings-upload-stamp-file');

  const usersTableBody = document.getElementById('settings-users-body');
  const showAddUserFormBtn = document.getElementById('settings-show-add-user-btn');
  const cancelAddUserBtn = document.getElementById('settings-cancel-add-user-btn');
  const submitAddUserBtn = document.getElementById('settings-submit-add-user-btn');
  const addUserFormBox = document.getElementById('settings-add-user-box');

  const saveAccountBtn = document.getElementById('settings-save-account-btn');
  const toastMsg = document.getElementById('settings-toast-msg');

  // Open Settings Modal
  if (openSettingsBtn) {
    openSettingsBtn.addEventListener('click', () => {
      openSettingsModal();
    });
  }

  // Event listener for opening settings
  window.addEventListener('hba:open-settings', () => {
    openSettingsModal();
  });

  function openSettingsModal() {
    if (!settingsModal) return;
    settingsModal.style.display = 'flex';
    requestAnimationFrame(() => {
      settingsModal.classList.add('active');
    });
    settingsModal.style.zIndex = '20000';
    document.body.style.overflow = 'hidden';

    // Init Tablet Signature Pad Canvas
    const canvas = document.getElementById('handwritten-sig-canvas');
    if (canvas) {
      if (!signaturePadInstance) {
        signaturePadInstance = new SignaturePad(canvas);
      }
    }

    setTimeout(() => {
      loadAllSettings();
    }, 60);
  }

  function closeSettingsModal() {
    if (!settingsModal) return;
    settingsModal.classList.remove('active');
    setTimeout(() => {
      settingsModal.style.display = 'none';
    }, 200);
    document.body.style.overflow = '';
  }

  if (settingsClose) {
    settingsClose.addEventListener('click', closeSettingsModal);
  }

  // Backdrop click to close
  settingsModal?.addEventListener('click', (e) => {
    if (e.target === settingsModal) {
      closeSettingsModal();
    }
  });

  // Switch Settings Tabs
  tabBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      tabBtns.forEach(b => b.classList.remove('active'));
      tabPanes.forEach(p => p.classList.remove('active'));

      btn.classList.add('active');
      const targetPane = document.getElementById(`pane-${btn.dataset.tab}`);
      if (targetPane) targetPane.classList.add('active');

      if (btn.dataset.tab === 'users') {
        fetchUsersList();
      }
    });
  });

  // Load All Settings from LocalStorage + Server Database
  async function loadAllSettings() {
    // 1. Fetch from Database API first
    try {
      const response = await fetch(`${SETTINGS_API}?action=get_settings`);
      const result = await response.json();

      if (result.success && result.data) {
        populateSettingsForm(result.data);
        return;
      }
    } catch (err) {}

    // Fallback to local storage if network is offline
    const savedStamp = localStorage.getItem('hba_company_stamp');
    const savedSig = localStorage.getItem('hba_user_signature');
    const savedUser = JSON.parse(localStorage.getItem('hba_current_user') || 'null');

    const stampPreview = document.getElementById('setting-stamp-preview');
    const stampPlaceholder = document.getElementById('setting-stamp-placeholder');
    if (stampPreview) {
      if (savedStamp && savedStamp.startsWith('data:image/')) {
        stampPreview.src = savedStamp;
        stampPreview.style.display = 'block';
        if (stampPlaceholder) stampPlaceholder.style.display = 'none';
      } else {
        stampPreview.src = '';
        stampPreview.style.display = 'none';
        if (stampPlaceholder) stampPlaceholder.style.display = 'block';
      }
    }

    if (savedSig && signaturePadInstance && savedSig.startsWith('data:image/')) {
      signaturePadInstance.fromDataURL(savedSig);
    }

    if (savedUser) {
      const uInput = document.getElementById('my-username-input');
      const fInput = document.getElementById('my-fullname-input');
      if (uInput) uInput.value = savedUser.username || '';
      if (fInput) fInput.value = savedUser.full_name || '';
    }
  }

  function populateSettingsForm(s) {
    if (s.bank_name) document.getElementById('setting-bank-name').value = s.bank_name;
    if (s.account_name) document.getElementById('setting-account-name').value = s.account_name;
    if (s.account_no) document.getElementById('setting-account-no').value = s.account_no;
    if (s.swift) document.getElementById('setting-swift').value = s.swift;
    if (s.iban) document.getElementById('setting-iban').value = s.iban;
    if (s.terms_text) document.getElementById('setting-terms-text').value = s.terms_text;

    const stampPreview = document.getElementById('setting-stamp-preview');
    const stampPlaceholder = document.getElementById('setting-stamp-placeholder');
    if (s.company_stamp && s.company_stamp.startsWith('data:image/')) {
      localStorage.setItem('hba_company_stamp', s.company_stamp);
      if (stampPreview) {
        stampPreview.src = s.company_stamp;
        stampPreview.style.display = 'block';
        if (stampPlaceholder) stampPlaceholder.style.display = 'none';
      }
    } else {
      localStorage.removeItem('hba_company_stamp');
      if (stampPreview) {
        stampPreview.src = '';
        stampPreview.style.display = 'none';
        if (stampPlaceholder) stampPlaceholder.style.display = 'block';
      }
    }

    if (s._current_user) {
      const u = s._current_user;
      localStorage.setItem('hba_current_user', JSON.stringify(u));
      const uInput = document.getElementById('my-username-input');
      const fInput = document.getElementById('my-fullname-input');
      if (uInput) uInput.value = u.username || '';
      if (fInput) fInput.value = u.full_name || '';

      if (u.signature_data && u.signature_data.length > 200) {
        localStorage.setItem('hba_user_signature', u.signature_data);
        if (signaturePadInstance) {
          setTimeout(() => {
            signaturePadInstance.fromDataURL(u.signature_data);
          }, 80);
        }
        const editorSig = document.getElementById('editor-sig-img');
        if (editorSig) {
          editorSig.src = u.signature_data;
          editorSig.style.display = 'block';
        }
      }
    }
  }

  // Toast Notification Message
  function showToast(message, type = 'success') {
    if (!toastMsg) return;
    toastMsg.textContent = message;
    toastMsg.style.display = 'block';
    toastMsg.style.background = type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)';
    toastMsg.style.color = type === 'success' ? '#10B981' : '#EF4444';
    toastMsg.style.border = type === 'success' ? '1px solid #10B981' : '1px solid #EF4444';

    setTimeout(() => {
      toastMsg.style.display = 'none';
    }, 4000);
  }

  // Save Signature Button (Canvas + Upload)
  if (saveSigBtn) {
    saveSigBtn.addEventListener('click', async () => {
      const sigData = signaturePadInstance?.toDataURL();
      if (!sigData || signaturePadInstance?.isEmpty) {
        showToast('Please draw your handwritten signature or upload a PNG image file first.', 'error');
        return;
      }

      // Persist in LocalStorage
      localStorage.setItem('hba_user_signature', sigData);
      const editorSig = document.getElementById('editor-sig-img');
      if (editorSig) editorSig.src = sigData;
      showToast('Handwritten Signature Saved Successfully ✓');

      // Sync to Database API
      try {
        await fetch(`${SETTINGS_API}?action=save_signature`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ signature_data: sigData })
        });
      } catch (err) {}
    });
  }

  // Clear Signature Canvas
  if (clearSigBtn) {
    clearSigBtn.addEventListener('click', () => {
      signaturePadInstance?.clear();
      showToast('Canvas cleared.', 'success');
    });
  }

  // Upload Signature Image File
  if (uploadSigInput) {
    uploadSigInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const sigData = event.target.result;
          signaturePadInstance?.fromDataURL(sigData);
          localStorage.setItem('hba_user_signature', sigData);
          showToast('Handwritten Signature Uploaded & Saved to Profile ✓', 'success');

          // Immediately persist to server database
          try {
            await fetch(`${SETTINGS_API}?action=save_signature`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ signature_data: sigData })
            });
          } catch (err) {
            console.error('Error auto-saving signature to DB:', err);
          }
        };
        reader.readAsDataURL(file);
      }
    });
  }

  // Upload Company Stamp Image File
  if (uploadStampInput) {
    uploadStampInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const stampDataUrl = event.target.result;
          localStorage.setItem('hba_company_stamp', stampDataUrl);

          const stampPreview = document.getElementById('setting-stamp-preview');
          if (stampPreview) {
            stampPreview.src = stampDataUrl;
            stampPreview.style.display = 'block';
          }

          showToast('Official Company Stamp Saved Successfully ✓');

          try {
            await fetch(`${SETTINGS_API}?action=save_settings`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ company_stamp: stampDataUrl })
            });
          } catch (err) {}
        };
        reader.readAsDataURL(file);
      }
    });
  }

  // Save My Account Profile & Password
  if (saveAccountBtn) {
    saveAccountBtn.addEventListener('click', async () => {
      const newUsername = document.getElementById('my-username-input')?.value.trim();
      const newFullName = document.getElementById('my-fullname-input')?.value.trim();
      const newPassword = document.getElementById('my-password-input')?.value.trim();

      if (!newUsername) {
        showToast('Username cannot be empty.', 'error');
        return;
      }

      try {
        const response = await fetch(`${SETTINGS_API}?action=update_profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: newUsername,
            full_name: newFullName,
            password: newPassword
          })
        });
        const res = await response.json();

        if (res.success) {
          const currentUser = JSON.parse(localStorage.getItem('hba_current_user') || '{}');
          currentUser.username = newUsername;
          currentUser.full_name = newFullName;
          localStorage.setItem('hba_current_user', JSON.stringify(currentUser));

          showToast('Account credentials updated in database successfully ✓', 'success');

          const pwInput = document.getElementById('my-password-input');
          if (pwInput) pwInput.value = '';

          const displayNameEl = document.getElementById('admin-user-display-name');
          if (displayNameEl) displayNameEl.textContent = newFullName || newUsername;

          fetchUsersList();
        } else {
          showToast(res.error || 'Failed to update credentials.', 'error');
        }
      } catch (err) {
        showToast('Network error updating credentials.', 'error');
      }
    });
  }

  // Save Company & Bank Details (Admin Privilege)
  if (saveBankBtn) {
    saveBankBtn.addEventListener('click', async () => {
      const payload = {
        bank_name: document.getElementById('setting-bank-name')?.value || '',
        account_name: document.getElementById('setting-account-name')?.value || '',
        account_no: document.getElementById('setting-account-no')?.value || '',
        swift: document.getElementById('setting-swift')?.value || '',
        iban: document.getElementById('setting-iban')?.value || '',
        terms_text: document.getElementById('setting-terms-text')?.value || ''
      };

      localStorage.setItem('hba_bank_settings', JSON.stringify(payload));
      showToast('Company & Bank Details Saved Successfully ✓');

      try {
        await fetch(`${SETTINGS_API}?action=save_settings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (err) {}
    });
  }

  // User Accounts List & In-Window Add Form
  async function fetchUsersList() {
    if (!usersTableBody) return;
    let usersList = JSON.parse(localStorage.getItem('hba_users_list') || 'null') || DEFAULT_USERS;

    try {
      const response = await fetch(`${SETTINGS_API}?action=list_users`);
      const result = await response.json();
      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        usersList = result.data;
        localStorage.setItem('hba_users_list', JSON.stringify(usersList));
      }
    } catch (err) {}

    renderUsersTable(usersList);
  }

  function renderUsersTable(users) {
    if (!usersTableBody) return;
    usersTableBody.innerHTML = '';

    users.forEach(u => {
      const row = document.createElement('tr');
      const isAdmin = u.role === 'admin';
      const badgeClass = isAdmin ? 'pill-badge purple' : 'pill-badge teal';

      row.innerHTML = `
        <td><strong style="color:var(--color-text-main);">${escapeHtml(u.username)}</strong></td>
        <td>${escapeHtml(u.full_name || 'Staff Member')}</td>
        <td><span class="${badgeClass}">${isAdmin ? 'Admin Privilege' : 'Staff'}</span></td>
        <td style="text-align:right;">
          ${u.id === 1 || u.username === 'admin' ? '<span style="font-size:0.75rem; color:var(--color-grey); font-weight:600;">Active Admin</span>' : `<button class="btn-action-delete delete-user-btn" data-id="${u.id}" title="Remove User" style="width:30px; height:30px; border-radius:6px; border:1px solid rgba(239, 68, 68, 0.25); background:rgba(239, 68, 68, 0.08); color:#EF4444; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.2s ease;"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg></button>`}
        </td>
      `;

      usersTableBody.appendChild(row);
    });

    usersTableBody.querySelectorAll('.delete-user-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = e.currentTarget.dataset.id;
        let users = JSON.parse(localStorage.getItem('hba_users_list') || '[]') || DEFAULT_USERS;
        users = users.filter(u => u.id != id);
        localStorage.setItem('hba_users_list', JSON.stringify(users));
        renderUsersTable(users);
        showToast('User removed.');

        try {
          await fetch(`${SETTINGS_API}?action=delete_user&id=${id}`, { method: 'POST' });
        } catch (err) {}
      });
    });
  }

  // In-Window Add New User Form Controls (No prompt popups!)
  if (showAddUserFormBtn && addUserFormBox) {
    showAddUserFormBtn.addEventListener('click', () => {
      addUserFormBox.style.display = 'block';
    });
  }

  if (cancelAddUserBtn && addUserFormBox) {
    cancelAddUserBtn.addEventListener('click', () => {
      addUserFormBox.style.display = 'none';
    });
  }

  if (submitAddUserBtn) {
    submitAddUserBtn.addEventListener('click', async () => {
      const uName = document.getElementById('new-user-username')?.value.trim();
      const fName = document.getElementById('new-user-fullname')?.value.trim() || 'Staff Member';
      const pWord = document.getElementById('new-user-password')?.value.trim();
      const uRole = document.getElementById('new-user-role')?.value || 'staff';

      if (!uName || !pWord) {
        showToast('Username and Password are required for new users.', 'error');
        return;
      }

      let usersList = JSON.parse(localStorage.getItem('hba_users_list') || 'null') || DEFAULT_USERS;
      const newUser = {
        id: Date.now(),
        username: uName,
        full_name: fName,
        role: uRole,
        created_at: new Date().toISOString().split('T')[0]
      };
      usersList.push(newUser);
      localStorage.setItem('hba_users_list', JSON.stringify(usersList));

      renderUsersTable(usersList);
      showToast(`User ${uName} created successfully! ✓`);

      if (addUserFormBox) addUserFormBox.style.display = 'none';
      document.getElementById('new-user-username').value = '';
      document.getElementById('new-user-password').value = '';
      document.getElementById('new-user-fullname').value = '';

      try {
        await fetch(`${SETTINGS_API}?action=create_user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: uName, full_name: fName, password: pWord, role: uRole })
        });
      } catch (err) {}
    });
  }
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m]));
}
